<template>
  <div class="page-container h-screen w-screen overflow-hidden flex flex-col">
    <!-- header area -->
    <header :class="{'hidden': !enableNav}">
      <Nav />
    </header>

    <div class="flex h-[calc(100vh-60px)]" :class="[{ 'h-full': !enableNav }]">
      <div
        ref="sidebarRef"
        class="sidebars flex flex-col"
        :class="[
          { 'collapsed': sidebarCollapsed }
        ]"
      >
        <!-- sidebar links -->
        <el-scrollbar class="w-full flex-1">
          <Sidebar :collapsed="sidebarCollapsed" />
        </el-scrollbar>

        <!-- toggle-icon -->
        <div
          class="toggle-sidebar"
          @click="toggleCollapseSidebars"
        >
          <svg-icon
            class="cursor-pointer hover:opacity-70 mr-3 !w-[14px] !h-[14px]"
            :icon="!sidebarCollapsed ? 'uiw:left' : 'uiw:right'"
          />
        </div>
      </div>

      <main class="page-content flex flex-col flex-1 overflow-hidden">
        <Tabs v-if="settings.showTopTabs" />

        <!-- main content -->
        <section class="main-content flex-1" :class="{'!pb-0': isPb0Routes}">
          <router-view v-slot="{ Component, route }">
            <transition
              name="fade-transform"
              mode="out-in"
            >
              <keep-alive :include="cachedRouteNames">
                <component
                  :is="Component"
                  v-if="!route.meta.link"
                  :key="route.path"
                />
                <div
                  v-else
                  class="h-full"
                >
                  <iframe
                    :src="route.meta.link as string"
                    frameborder="no"
                    style="width: 100%;height: 100%"
                    scrolling="auto"
                  />
                </div>
              </keep-alive>
            </transition>
          </router-view>
        </section>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import Nav from './nav.vue';
import Tabs from './tabs.vue';
import Sidebar from './sidebar.vue';
import { SvgIcon } from '@vue3/components';
import { settings } from '@core/api';
import { computed, onBeforeMount, ref } from 'vue';
import { useStorage, useWindowSize } from '@vueuse/core';
import { pb0routes, useTabsStore } from '../..';
import { useRoute } from 'vue-router';
import { isNavEnabled } from '@core/api';

defineOptions({ name: 'CommonLayout' });

const enableNav = computed(() => isNavEnabled());

const sidebarRef = ref<HTMLDivElement>(null);
const currentRoute = useRoute();

const { width } = useWindowSize();

const tabsStore = useTabsStore();

const isCollapsed = useStorage(settings.sidebarCollapseKey, false);

const smallScreen = computed(() => width.value <= 1024);
const cachedRouteNames = computed(() => tabsStore.cachedRoutes.map((r) => r.name));
const sidebarCollapsed = computed(() => isCollapsed.value);
const isPb0Routes = computed(() => pb0routes.includes(currentRoute.name as string));

function toggleCollapseSidebars() {
  isCollapsed.value = !sidebarCollapsed.value;
}

onBeforeMount(() => {
  if (smallScreen.value) {
    isCollapsed.value = true;
  }
});
</script>

<style lang="scss" scoped>
.sidebars {
  @apply relative box-border w-[200px] transition-all;

  background-color: var(--color-bg-base);
  border-right: 1px var(--sidebar-border-color) solid;

  :deep(.el-menu) {
    border-right: none;
  }

  &.collapsed {
    width: 58px;

    :deep(.svg-icon) {
      @apply m-0 flex-1;

      color: var(--color-text-primary);
    }

    :deep(.el-menu--collapse) {
      width: 58px !important;
    }

    :deep(.el-sub-menu.is-active) {
      & > .el-sub-menu__title {
        & > .svg-icon {
          color: var(--color-primary) !important;
        }
      }
    }

    :deep(.el-icon) {
      display: none;
    }

    :deep(.el-menu-item),
    :deep(.el-sub-menu__title) {
      padding: 0 !important;

      span {
        display: none;
      }
    }
  }

  :deep(.v-leave-active) {
    .svg-icon {
      display: block !important;
    }

    :deep(.el-icon) {
      display: none;
    }

    .el-menu-item span,
    .el-sub-menu__title span {
      display: none;
    }
  }

  :deep(.el-menu--collapse.v-leave-active) {
    width: 100%;

    .svg-icon {
      display: none !important;
    }
  }

  &.sidebars-fiexd {
    @apply fixed left-0 h-screen z-20 top-0;

    &.collapsed {
      left: -57px;
    }
  }
}

.main-content {
  @apply text-[14px] overflow-y-auto overflow-x-hidden px-[16px] pt-[16px] pb-[32px] box-border relative;

  background-color: var(--color-gray-bg);
}

.toggle-sidebar {
  @apply absolute top-[236px] right-[-17px] h-[40px] w-[16px] z-50 cursor-pointer;

  border-color: var(--color-border-base);
  background-color: var(--color-bg-base);
  box-shadow: 3px 0px 5px rgb(51, 51, 51, .18);

  .svg-icon {
    @apply m-0 absolute top-[12px] left-0;

    color: var(--color-text-primary);
  }
}
</style>
